import { markRaw, ref } from 'vue'

import { InstatutionData } from '@/views/LinkedData/utils/types'

import { CONNECTOR_INSTRUCTIONS } from '@/views/LinkedData/utils/instructions'

import { connectAccount } from '@/views/LinkedData/Connectors/components/utils/helpers'

import { useLinkedDataConnector } from '@/hooks/linkedDataConnector'

import { useModalsStore } from '@/store/modals'

import ConnectorAkoya from '@/modules/Connectors/ConnectorAkoya.vue'
import ConnectorInstructions from '@/modules/Connectors/ConnectorInstructions.vue'

type Options = {
  /**
   * Hide the surface the user clicked from, so a hand-off dialog (instructions,
   * Akoya, Plaid Link, Yodlee FastLink) is not stacked on top of it.
   */
  hide: () => void
  /** Bring that surface back when the user cancels the hand-off. */
  show: () => void
}

/**
 * Connecting one institution: pick the connector from `source`, show the
 * institution's instructions first when it has any, and hand off.
 *
 * Extracted from AddAccount.vue so the @ds Add asset dialog and the legacy
 * connect dialog dispatch identically. Both are call sites; neither owns it.
 * Behaviour is unchanged from the original — only `modal.close()` / `modal.show()`
 * became the `hide` / `show` options, because the @ds dialog is a Reka dialog
 * with no ModalClass to call.
 */
export const useInstitutionConnect = ({ hide, show }: Options) => {
  const modalsStore = useModalsStore()

  const loading = ref(false)

  const { plaid, yodlee } = useLinkedDataConnector((flag: boolean) =>
    flag ? show() : hide(),
  )

  const connectWithPlaid = (
    institutionId?: string,
    routingNumber?: string | null,
  ) => {
    loading.value = true
    plaid(institutionId, routingNumber).finally(() => (loading.value = false))
  }

  const connectWithYodlee = (institutionId?: string) => {
    loading.value = true
    yodlee(institutionId).finally(() => (loading.value = false))
  }

  const proceedWithInstitution = async (item: InstatutionData) => {
    switch (item.source) {
      case 'plaid':
        connectWithPlaid(item.institution_id, item.routing_number)
        break
      case 'yodlee':
        loading.value = true
        await connectWithYodlee(item.institution_id)
        loading.value = false
        break
      case 'akoya':
        loading.value = true
        connectAccount(item.source, item)
          .then(({ name, url, logo }) => {
            const modalInstance = modalsStore.init(
              'add-account-akoya-dialog',
              markRaw(ConnectorAkoya),
            )
            modalInstance?.open(modalsStore.getZIndex(), {
              name,
              url,
              logo,
              onCancel: show,
            })
          })
          .finally(() => {
            loading.value = false
          })
        break
      default:
        loading.value = true
        connectAccount(item.source, item)
          .then(({ url }) => {
            location.href = url
          })
          .finally(() => {
            loading.value = false
          })
    }
  }

  const connectInstitution = (item: InstatutionData) => {
    const instructions = CONNECTOR_INSTRUCTIONS[item.institution_id]
    if (!instructions) {
      proceedWithInstitution(item)
      return
    }

    hide()
    const modalInstance = modalsStore.init(
      'add-account-instructions-dialog',
      markRaw(ConnectorInstructions),
    )
    modalInstance?.open(modalsStore.getZIndex(), {
      instructions,
      onCancel: show,
      onContinue: () => proceedWithInstitution(item),
    })
  }

  return {
    loading,

    connectInstitution,
    connectWithPlaid,
    connectWithYodlee,
  }
}
